// Modal: shows top-N PK formula candidates for an Rx and lets the tech pick one
// (or clear the current match). Fetches GET /api/rx/{id}/formula-candidates on open.

import { useEffect, useState } from "react";
import { apiGet, apiPost } from "../lib/api";
import type { FormulaCandidate } from "@shared/types";

type Props = {
  rxId: number;
  currentFormulaId: number | null;
  onPicked: () => void;       // called after successful pick/clear; parent refetches
  onCancel: () => void;
};

export function FormulaPicker({ rxId, currentFormulaId, onPicked, onCancel }: Props) {
  const [candidates, setCandidates] = useState<FormulaCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    apiGet<FormulaCandidate[]>(`/rx/${rxId}/formula-candidates?topN=8`, ctrl.signal)
      .then(setCandidates)
      .catch((e) => setError(String(e)));
    return () => ctrl.abort();
  }, [rxId]);

  async function pick(c: FormulaCandidate) {
    setBusy(true);
    try {
      await apiPost(`/rx/${rxId}/formula`, { formulaId: c.formulaId, storeId: c.storeId });
      onPicked();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    try {
      await apiPost(`/rx/${rxId}/formula`, { clear: true });
      onPicked();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg shadow-xl w-[640px] max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-center">
          <h2 className="text-lg font-semibold">Pick PK formula</h2>
          <span className="ml-auto text-xs text-ink-soft">
            Top candidates from the store's Drug/Formula library
          </span>
        </div>

        <div className="overflow-auto px-5 py-3 flex-1">
          {error && <div className="bg-bad-soft text-bad px-3 py-2 rounded mb-3 text-sm">{error}</div>}
          {!candidates ? (
            <div className="text-ink-soft text-sm py-4">Searching…</div>
          ) : candidates.length === 0 ? (
            <div className="text-ink-soft text-sm py-4">
              No matches in the formula library. Either no exports loaded yet for this store, or the
              extracted drug doesn't match any active formula. Tech will need to add it in PK first.
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {candidates.map((c) => (
                <li key={`${c.storeId}-${c.formulaId}`} className="border border-border rounded p-3 hover:border-accent">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <strong className="text-sm">{c.name}</strong>
                        {c.formulaId === currentFormulaId && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-good-soft text-good">currently linked</span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                          c.drugType === "C" ? "bg-accent-soft text-accent" :
                          c.drugType === "B" ? "bg-pill text-ink-soft" :
                                               "bg-pill text-ink-soft"
                        }`}>
                          {c.drugType === "C" ? "Compound" : c.drugType === "B" ? "Brand" : "Generic"}
                        </span>
                        {c.schedule && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-warn-soft text-warn">CV {c.schedule}</span>
                        )}
                      </div>
                      <div className="text-xs text-ink-soft mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        {c.form     && <span>Form: <strong className="text-ink">{c.form}</strong></span>}
                        {c.strength && <span>Strength: <strong className="text-ink">{c.strength}</strong></span>}
                        {c.speedCode && <span>SpeedCode: <strong className="text-ink">{c.speedCode}</strong></span>}
                        <span>Formula #{c.formulaId}</span>
                      </div>
                      {c.reasons.length > 0 && (
                        <div className="text-[11px] text-ink-soft mt-1.5">
                          Matched on: {c.reasons.join(" · ")}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        c.confidence >= 0.85 ? "bg-good-soft text-good" :
                        c.confidence >= 0.6  ? "bg-warn-soft text-warn" :
                                               "bg-bad-soft text-bad"
                      }`}>
                        {c.confidence.toFixed(2)}
                      </span>
                      <button
                        type="button"
                        onClick={() => pick(c)}
                        disabled={busy}
                        className="px-3 py-1 bg-accent text-white rounded text-xs font-medium hover:brightness-95 disabled:opacity-40"
                      >
                        {c.formulaId === currentFormulaId ? "Confirm" : "Pick"}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center gap-2">
          {currentFormulaId !== null && (
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              className="px-3 py-1.5 border border-bad text-bad rounded text-sm hover:bg-bad-soft disabled:opacity-40"
            >
              Clear formula
            </button>
          )}
          <div className="flex-1" />
          <button type="button" onClick={onCancel} disabled={busy} className="px-3 py-1.5 border border-border rounded text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
